/**
 * Aba "Valores" do detalhe do ticket (v9): lançamentos financeiros
 * (deslocamentos, serviços avulsos e valores extras) com total do ticket no
 * rodapé e "+ Lançamento" (dialog: tipo → cadastro filtrado pelo cliente do
 * ticket → qtd/valor). Excluir com confirmação. Botões escondidos sem
 * tickets.edit (a API barra de verdade).
 *
 * "Nova cotação" (PoolTrack, 04/10): o km de ida e de volta com o preço por km
 * digitado; o "Lançar" da cotação cria um lançamento por perna (quantidade =
 * km, unitário = R$/km), então o total do ticket já os inclui. A linha do
 * lançamento que veio de uma cotação mostra "11,1 km × R$ 1,50/km", e a lista
 * de cotações fica abaixo, como registro.
 *
 * Valor cheio (05/10): a cotação com o valor cheio digitado cria UM lançamento
 * (1 × o valor, com o km de cada perna no nome), que aparece como qualquer
 * lançamento; na lista de cotações, o km de cada perna e o por km de
 * referência, quando houver.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Plus, Route, Trash2 } from 'lucide-react';
import {
  CHARGE_KIND_LABELS,
  GOOGLE_LATLNG_CACHE_DAYS,
  QUOTE_LEG_LABELS,
  SERVICE_KIND_LABELS,
  chargeTotalCents,
  quoteDirectionsUrl,
  quoteFullAmountEnabled,
  quotePointText,
  type ChargeKind,
  type QuoteLegKey,
  type ServiceKind,
  type TicketCharge,
  type TicketDetail,
  type TicketQuote,
} from '@erp/shared';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge, Dialog, Spinner } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  displacementsOptions,
  errorMessage,
  extraValuesOptions,
  looseServicesOptions,
} from '@/features/billing/billing-queries';
import {
  centsToMoney,
  formatBRL,
  formatBRLInput,
  moneyToCents,
  parseBRLInput,
} from '@/features/billing/billing-format';
import { MoneyInput } from '@/features/billing/contract-drawer';
import { ticketChargesQuery, ticketQuotesQuery } from './tickets-queries';
import { useCreateTicketCharge, useDeleteTicketCharge } from './tickets-mutations';
import { formatDate, formatTime } from './detail-format';
import { isTicketMirrored } from './ticket-format';
import { QuoteDialog } from './quote-dialog';
import { formatKm } from './quote-format';

/** '2.00' → '2' / '2.50' → '2,5' (quantidade em pt-BR, sem zeros à direita). */
function formatQuantity(quantity: string): string {
  const n = Number(quantity);
  if (!Number.isFinite(n)) return quantity;
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export function ValoresTab({ ticket }: { ticket: TicketDetail }) {
  const { can } = usePermissions();
  // spec 011: em ticket espelhado a API recusa lançar/excluir (409)
  const canEdit = can('tickets.edit') && !isTicketMirrored(ticket);
  const chargesQ = useQuery(ticketChargesQuery(ticket.ticketNumber));
  const quotesQ = useQuery(ticketQuotesQuery(ticket.ticketNumber));
  const deleteCharge = useDeleteTicketCharge(ticket.ticketNumber);

  const [launchOpen, setLaunchOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TicketCharge | null>(null);

  const charges = chargesQ.data ?? [];
  const totalCents = charges.reduce((acc, c) => acc + moneyToCents(c.total), 0);
  const quotes = quotesQ.data?.items ?? [];
  // lançamento → a perna da cotação que o criou (a linha mostra km × R$/km)
  const quoteLegOf = new Map<number, { quoteId: number; leg: QuoteLegKey }>();
  for (const q of quotes) {
    if (q.ida.chargeId !== null) quoteLegOf.set(q.ida.chargeId, { quoteId: q.id, leg: 'ida' });
    if (q.volta.chargeId !== null) quoteLegOf.set(q.volta.chargeId, { quoteId: q.id, leg: 'volta' });
  }

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <h2 className="text-base font-semibold">Valores</h2>
          {canEdit ? (
            <div className="flex items-center gap-2">
              {/* só com a lista carregada: ela diz se há cálculo de km. Se a API ainda
                  não tem a rota (o SPA publica antes da API), o botão some e o
                  resto da aba segue igual — mudanca-de-dois-lados.md */}
              {quotesQ.isSuccess ? (
                <Button size="sm" variant="outline" onClick={() => setQuoteOpen(true)}>
                  <Route className="size-4" />
                  Nova cotação
                </Button>
              ) : null}
              <Button size="sm" onClick={() => setLaunchOpen(true)}>
                <Plus className="size-4" strokeWidth={2.5} />
                Lançamento
              </Button>
            </div>
          ) : null}
        </div>

        {chargesQ.isPending ? (
          <div className="space-y-3 p-4" aria-busy>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : chargesQ.isError ? (
          <p className="p-4 text-sm text-muted-foreground">
            Erro ao carregar lançamentos: {errorMessage(chargesQ.error)}
          </p>
        ) : charges.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Nenhum lançamento neste ticket.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="px-4 text-xs font-semibold">Tipo</TableHead>
                <TableHead className="text-xs font-semibold">Nome</TableHead>
                <TableHead className="text-xs font-semibold">Qtd</TableHead>
                <TableHead className="text-xs font-semibold">Valor unitário</TableHead>
                <TableHead className="text-xs font-semibold">Total</TableHead>
                <TableHead className="text-xs font-semibold">Lançado por</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {charges.map((c) => {
                const fromQuote = quoteLegOf.get(c.id);
                return (
                  <TableRow key={c.id} className="h-10">
                    <TableCell className="px-4 text-sm whitespace-nowrap">
                      {CHARGE_KIND_LABELS[c.kind]}
                      {c.serviceKind ? (
                        <span className="text-muted-foreground">
                          {' '}
                          · {SERVICE_KIND_LABELS[c.serviceKind]}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-64">
                      <span className="block truncate text-sm" title={c.name}>
                        {c.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums whitespace-nowrap">
                      {fromQuote ? formatKm(c.quantity) : formatQuantity(c.quantity)}
                      {c.kind === 'service' ? ' h' : ''}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums whitespace-nowrap">
                      {formatBRL(c.unitValue)}
                      {fromQuote ? <span className="text-muted-foreground">/km</span> : null}
                    </TableCell>
                    <TableCell className="text-sm font-medium tabular-nums whitespace-nowrap">
                      {formatBRL(c.total)}
                      {fromQuote ? (
                        <span className="font-normal text-muted-foreground"> ({formatKm(c.quantity)})</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-56">
                      <span className="block truncate text-sm text-muted-foreground">
                        {c.createdByName ?? '—'} · {formatDate(c.createdAt)}{' '}
                        {formatTime(c.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground hover:text-destructive"
                          title="Excluir lançamento"
                          onClick={() => setDeleteTarget(c)}
                        >
                          <Trash2 />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="px-4 text-sm font-semibold">
                  Total do ticket
                </TableCell>
                <TableCell className="text-sm font-semibold tabular-nums whitespace-nowrap">
                  {formatBRL(centsToMoney(totalCents))}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </section>

      <QuotesSection quotes={quotes} />

      {launchOpen ? (
        <LaunchChargeDialog ticket={ticket} onClose={() => setLaunchOpen(false)} />
      ) : null}

      {quoteOpen && quotesQ.data ? (
        <QuoteDialog
          ticket={ticket}
          mapsAvailable={quotesQ.data.mapsAvailable}
          fullAmountAvailable={quoteFullAmountEnabled(quotesQ.data)}
          onClose={() => setQuoteOpen(false)}
        />
      ) : null}

      {deleteTarget ? (
        <Dialog open onClose={() => setDeleteTarget(null)} title="Excluir lançamento">
          <p className="text-sm text-muted-foreground">
            Excluir o lançamento &quot;{deleteTarget.name}&quot; (
            {formatBRL(deleteTarget.total)})? Esta ação não pode ser desfeita.
          </p>
          {deleteCharge.isError ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(deleteCharge.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteCharge.isPending}
              onClick={() =>
                deleteCharge.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })
              }
            >
              Excluir
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

const SERVICE_KINDS: ServiceKind[] = ['internal', 'remote', 'external'];

/**
 * As cotações do ticket, mais recente primeiro — o REGISTRO (o que foi digitado,
 * o km de cada perna, de onde ele veio, o preço, quem e quando). O dinheiro já
 * está nos lançamentos acima; aqui fica o porquê.
 *
 * Cotação antiga (F-112): a retenção apagou o que veio do Google (posições,
 * endereços encontrados, traçado). A linha mostra o digitado, o km e o dinheiro
 * como sempre, e o link da rota sai do texto e do lugar escolhido. Nenhum mapa.
 */
function QuotesSection({ quotes }: { quotes: TicketQuote[] }) {
  // carregando, sem cotações ou com erro: a seção não aparece (o dinheiro está
  // nos lançamentos acima, que não dependem dela)
  if (quotes.length === 0) return null;
  return (
    <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="border-b p-4">
        <h2 className="text-base font-semibold">Cotações de deslocamento</h2>
      </div>
      <ul className="divide-y">
        {quotes.map((q) => {
          const directionsUrl = quoteDirectionsUrl(q);
          return (
            <li key={q.id} className="space-y-2 p-4 text-sm" data-testid="quote-row">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-semibold">Cotação #{q.id}</span>
                <span className="text-muted-foreground">
                  {formatDate(q.createdAt)} {formatTime(q.createdAt)} · {q.createdBy?.name ?? '—'}
                  {q.technician ? ` · técnico ${q.technician.name}` : ''}
                </span>
                {q.pricingMode === 'full' ? (
                  <Badge variant="info" data-testid="quote-full-badge">
                    valor cheio
                  </Badge>
                ) : null}
                {q.pricingMode === 'full' && q.fullChargeId === null ? (
                  <Badge variant="destructive">lançamento excluído</Badge>
                ) : null}
                <span className="ml-auto font-semibold tabular-nums">{formatBRL(q.total)}</span>
              </div>
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">A</span> {quotePointText(q.origin)}{' '}
                → <span className="font-medium text-foreground">B</span>{' '}
                {quotePointText(q.destination)} →{' '}
                {q.returnTo ? (
                  <>
                    <span className="font-medium text-foreground">C</span>{' '}
                    {quotePointText(q.returnTo)}
                  </>
                ) : (
                  'volta à origem'
                )}
              </p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {(['ida', 'volta'] as const).map((key) => {
                  const leg = q[key];
                  const full = q.pricingMode === 'full';
                  return (
                    <div key={key} className="rounded-md border bg-muted/30 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{QUOTE_LEG_LABELS[key]}</span>
                        <span className="tabular-nums">
                          {formatKm(leg.km)}
                          {leg.pricePerKm !== null && leg.subtotal !== null ? (
                            <>
                              {' '}
                              × {formatBRL(leg.pricePerKm)}/km = {formatBRL(leg.subtotal)}
                              {full ? <span className="text-muted-foreground"> (referência)</span> : null}
                            </>
                          ) : null}
                        </span>
                        <Badge variant={leg.provider === 'manual' ? 'warning' : 'info'}>
                          {leg.provider === 'manual' ? 'km à mão' : leg.provider === 'google' ? 'Google' : 'teste'}
                        </Badge>
                        {full ? null : leg.charged && leg.chargeId === null ? (
                          <Badge variant="destructive">lançamento excluído</Badge>
                        ) : !leg.charged ? (
                          <Badge>sem lançamento (R$ 0)</Badge>
                        ) : null}
                      </div>
                      {leg.manualReason ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">Motivo: {leg.manualReason}</p>
                      ) : (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {leg.meters.toLocaleString('pt-BR')} m exatos
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {directionsUrl ? (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="size-3" />
                    Ver rota no Google Maps
                  </a>
                ) : null}
                {q.googlePurgedAt ? (
                  <span className="text-xs text-muted-foreground" data-testid="quote-google-purged">
                    Posições e endereços do Google apagados (os termos do Google limitam a{' '}
                    {GOOGLE_LATLNG_CACHE_DAYS} dias) — ficam o digitado, o km e os valores.
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Dialog "+ Lançamento": Tipo → cadastro (só os do cliente do ticket ou
 * Todos) → [serviço: Interno/Remoto/Externo preenche o valor-hora] →
 * Quantidade/Horas → Valor unitário (prefixado do cadastro, editável).
 */
function LaunchChargeDialog({ ticket, onClose }: { ticket: TicketDetail; onClose: () => void }) {
  const create = useCreateTicketCharge(ticket.ticketNumber);

  const [kind, setKind] = useState<'' | ChargeKind>('');
  const [sourceId, setSourceId] = useState<number | ''>('');
  const [serviceKind, setServiceKind] = useState<'' | ServiceKind>('');
  const [quantityText, setQuantityText] = useState('1');
  const [unitText, setUnitText] = useState('');

  const displacementsQ = useQuery({
    ...displacementsOptions(),
    enabled: kind === 'displacement',
  });
  const servicesQ = useQuery({ ...looseServicesOptions(), enabled: kind === 'service' });
  const extrasQ = useQuery({ ...extraValuesOptions(), enabled: kind === 'extra' });

  // Cadastro escopado: só os do cliente do ticket ou "Todos" (clientId null).
  const forTicketClient = (row: { clientId: number | null }) =>
    row.clientId === null || row.clientId === ticket.client.id;

  const displacements = (displacementsQ.data ?? []).filter(forTicketClient);
  const services = (servicesQ.data ?? []).filter(forTicketClient);
  const extras = extrasQ.data ?? [];

  const sourceQ = kind === 'displacement' ? displacementsQ : kind === 'service' ? servicesQ : extrasQ;
  const selectedService =
    kind === 'service' && sourceId !== '' ? (services.find((s) => s.id === sourceId) ?? null) : null;

  /** Valor default do cadastro escolhido (p/ serviço depende do serviceKind). */
  const defaultUnitFor = (
    nextKind: ChargeKind,
    nextSourceId: number,
    nextServiceKind: '' | ServiceKind
  ): string => {
    if (nextKind === 'displacement') {
      const d = displacements.find((x) => x.id === nextSourceId);
      return d ? formatBRLInput(d.value) : '';
    }
    if (nextKind === 'extra') {
      const v = extras.find((x) => x.id === nextSourceId);
      return v ? formatBRLInput(v.unitValue) : '';
    }
    const s = services.find((x) => x.id === nextSourceId);
    if (!s || nextServiceKind === '') return '';
    const value =
      nextServiceKind === 'internal'
        ? s.internalHourValue
        : nextServiceKind === 'remote'
          ? s.remoteHourValue
          : s.externalHourValue;
    return formatBRLInput(value);
  };

  const quantity = parseBRLInput(quantityText);
  const unitValue = parseBRLInput(unitText);
  const valid =
    kind !== '' &&
    sourceId !== '' &&
    (kind !== 'service' || serviceKind !== '') &&
    quantity !== null &&
    Number(quantity) > 0;

  const submit = () => {
    if (kind === '' || sourceId === '' || quantity === null || Number(quantity) <= 0) return;
    if (kind === 'service' && serviceKind === '') return;
    create.mutate(
      {
        kind,
        sourceId,
        serviceKind: kind === 'service' && serviceKind !== '' ? serviceKind : undefined,
        quantity,
        unitValue: unitValue ?? undefined,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Novo lançamento">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="charge-kind">
            Tipo <span className="text-destructive">*</span>
          </Label>
          <Select
            id="charge-kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as '' | ChargeKind);
              setSourceId('');
              setServiceKind('');
              setUnitText('');
            }}
            autoFocus
          >
            <option value="" disabled>
              Selecione…
            </option>
            <option value="displacement">Deslocamento</option>
            <option value="service">Serviço avulso</option>
            <option value="extra">Valor extra</option>
          </Select>
        </div>

        {kind !== '' ? (
          <div className="space-y-1.5">
            <Label htmlFor="charge-source">
              {CHARGE_KIND_LABELS[kind]} <span className="text-destructive">*</span>
            </Label>
            {sourceQ.isPending ? (
              <div className="flex h-9 items-center px-1">
                <Spinner className="size-4" />
              </div>
            ) : (
              <Select
                id="charge-source"
                value={sourceId === '' ? '' : String(sourceId)}
                onChange={(e) => {
                  const next = e.target.value === '' ? '' : Number(e.target.value);
                  setSourceId(next);
                  if (next !== '') setUnitText(defaultUnitFor(kind, next, serviceKind));
                }}
              >
                <option value="" disabled>
                  Selecione…
                </option>
                {(kind === 'displacement' ? displacements : kind === 'service' ? services : extras).map(
                  (row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  )
                )}
              </Select>
            )}
            {sourceQ.isError ? (
              <p className="text-xs text-destructive">
                Erro ao carregar cadastros: {errorMessage(sourceQ.error)}
              </p>
            ) : !sourceQ.isPending &&
              (kind === 'displacement' ? displacements : kind === 'service' ? services : extras)
                .length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nenhum cadastro disponível para o cliente deste ticket.
              </p>
            ) : null}
          </div>
        ) : null}

        {kind === 'service' && sourceId !== '' ? (
          <div className="space-y-1.5">
            <Label htmlFor="charge-service-kind">
              Tipo de hora <span className="text-destructive">*</span>
            </Label>
            <Select
              id="charge-service-kind"
              value={serviceKind}
              onChange={(e) => {
                const next = e.target.value as '' | ServiceKind;
                setServiceKind(next);
                setUnitText(defaultUnitFor('service', sourceId, next));
              }}
            >
              <option value="" disabled>
                Selecione…
              </option>
              {SERVICE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {SERVICE_KIND_LABELS[k]}
                  {selectedService
                    ? ` — ${formatBRL(
                        k === 'internal'
                          ? selectedService.internalHourValue
                          : k === 'remote'
                            ? selectedService.remoteHourValue
                            : selectedService.externalHourValue
                      )}/h`
                    : ''}
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        {kind !== '' ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="charge-quantity">
                {kind === 'service' ? 'Horas' : 'Quantidade'}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="charge-quantity"
                value={quantityText}
                onChange={(e) => setQuantityText(e.target.value)}
                inputMode="decimal"
                placeholder={kind === 'service' ? 'Ex.: 1,5' : 'Ex.: 1'}
                aria-invalid={
                  (quantityText.trim() !== '' && quantity === null) || undefined
                }
                className="text-right tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="charge-unit">Valor unitário</Label>
              <MoneyInput id="charge-unit" value={unitText} onChange={setUnitText} />
            </div>
          </div>
        ) : null}

        {quantity !== null && unitValue !== null && kind !== '' ? (
          <p className="text-right text-sm text-muted-foreground">
            Total:{' '}
            <span className="font-semibold text-foreground tabular-nums">
              {formatBRL(centsToMoney(chargeTotalCents(quantity, unitValue)))}
            </span>
          </p>
        ) : null}

        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={create.isPending || !valid}>
            {create.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Lançar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
